import { LoggingService } from '@adaptiva/core/logging';
import { computed, inject, Injectable, linkedSignal, Signal, WritableSignal } from '@angular/core';
import { Observable, tap, throwError } from 'rxjs';
import { LlmConversationNode } from '../../classes';
import { ICreateForkRequest, IDeleteForkRequest, ILlmConversation, ILlmConversationNode, ILlmForkRequest, IRenameForkRequest } from '../../interfaces';
import { AidaService } from '../aida';
import { LlmClient } from '../llm-client';

@Injectable()
export class LlmForkService extends LlmClient {

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly logger: LoggingService = inject(LoggingService);

	public readonly forks: Signal<LlmConversationNode[]> = computed(() => {
		const _root: LlmConversationNode = this.aida.conversationTree();
		const _conversation: ILlmConversation = this.aida.conversation();

		if (!_root)
			return [];

		return this._getForks(_root);
	});

	public readonly forkIds: Signal<Set<string>> = computed(() => {
		return new Set(this.forks().map(f => f.nodeId));
	});

	public readonly activeFork: WritableSignal<LlmConversationNode> = linkedSignal(() => {
		const _forks: LlmConversationNode[] = this.forks();
		const _conversation: ILlmConversation = this.aida.conversation();

		if (!_forks.length)
			return null;

		const _activeFork: LlmConversationNode = _forks.find(f => f.forkName === _conversation.lastViewedFork);

		if (!_activeFork) {
			this.logger.error(
				'LlmForkService.activeFork',
				`Unable to find Fork: [${_conversation.lastViewedFork}]. Defaulting activeFork.`
			)();

			return _forks[0];
		}

		return _activeFork;
	});

	public readonly activeForkId: Signal<string | null> = computed(() => this.activeFork()?.nodeId || null);

	public createFork(_request: ICreateForkRequest): Observable<ILlmConversationNode> {
		const _url: string = `${this.getBaseUrl()}/create-fork`;

		if (!_request)
			return throwError(() => new Error('Creation request is empty'));

		return this.post(_url, _request);
	}

	public changeFork(_request: ILlmForkRequest): Observable<ILlmConversationNode> {
		if (!_request)
			return throwError(() => new Error('Request is empty'));

		const _url: string = `${this.getBaseUrl()}/change-fork`;

		return this.put(
			_url,
			{ conversationId: _request.conversationId, switchToFork: _request.forkName }
		).pipe(

		);
	}

	public renameFork(_request: IRenameForkRequest, _targetNodeId: string): Observable<void> {
		if (!_request)
			return throwError(() => new Error('Rename request is empty'));

		const _url: string = `${this.getBaseUrl()}/rename-fork`;

		return this.put(_url, _request).pipe(
			tap(() => {
				let _targetNode: LlmConversationNode = this.aida.getConversationNode(_targetNodeId);

				while (_targetNode) {
					_targetNode.forkName = _request.newForkName;

					if (_targetNode.parent?.children.length < 2)
						_targetNode = _targetNode.parent;
					else
						_targetNode = null;
				}
			})
		);
	}

	public deleteFork(_forkNode: LlmConversationNode): Observable<void> {
		if (!_forkNode)
			return throwError(() => new Error('No fork node provided'));

		const _url: string = `${this.getBaseUrl()}/delete-fork`;

		const _request: IDeleteForkRequest = {
			conversationId: this.aida.conversationId(),
			forkToDelete: _forkNode.forkName
		};

		return this.put(_url, _request).pipe(
			tap(() => {
				let _targetNode: LlmConversationNode = _forkNode;

				while (_targetNode) {
					if (_targetNode.parent?.children.length < 2)
						_targetNode = _targetNode.parent;
					else {
						this.aida.removeConversationNode(_targetNode.nodeId);
						_targetNode = null;
					}
				}
			})
		);
	}

	private _getForks(_target: LlmConversationNode, _forks: LlmConversationNode[] = []): LlmConversationNode[] {
		if (_target == null)
			return _forks;

		if (!_target.children?.length)
			_forks.push(_target);
		else {
			for (const _child of _target.children) {
				if (!_child?.children?.length)
					_forks.push(_child);
				else
					this._getForks(_child, _forks);
			}
		}

		return _forks;
	}

}
